import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { SURFACES } from '../../utils/prdTickets';

export const renum = (rows) => rows.map((r, i) => ({ ...r, num: i + 1 }));

export const cellField = {
  variant: 'standard',
  fullWidth: true,
  multiline: true,
  size: 'small',
  InputProps: { disableUnderline: false, sx: { fontSize: 13 } },
};

export function SectionTable({ title, caption, sx, rows, columns, onChange, onAdd }) {
  const update = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const remove = (i) => onChange(renum(rows.filter((_, j) => j !== i)));
  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 2, ...sx }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
      {caption && <Typography variant="caption" color="text.secondary">{caption}</Typography>}
      <Table size="small" sx={{ mt: 0.5 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 50 }}>{columns.numLabel}</TableCell>
            <TableCell>Details</TableCell>
            <TableCell sx={{ width: 40 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i}>
              <TableCell>{r.num ?? i + 1}</TableCell>
              <TableCell>
                <TextField {...cellField} value={r.details || ''} onChange={(e) => update(i, { details: e.target.value })} />
              </TableCell>
              <TableCell>
                <IconButton size="small" aria-label="delete row" onClick={() => remove(i)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Button size="small" startIcon={<AddIcon />} onClick={onAdd} sx={{ mt: 1 }}>Add row</Button>
    </Paper>
  );
}

export function BulletList({ title, items, onChange, placeholder }) {
  const update = (i, v) => onChange(items.map((x, j) => (j === i ? v : x)));
  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {items.map((x, i) => (
          <Box component="li" key={i} sx={{ mb: 0.5 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField {...cellField} value={x} placeholder={placeholder} onChange={(e) => update(i, e.target.value)} />
              <IconButton size="small" aria-label="delete bullet" onClick={() => onChange(items.filter((_, j) => j !== i))}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>
        ))}
      </Box>
      <Button size="small" startIcon={<AddIcon />} onClick={() => onChange([...items, ''])} sx={{ mt: 1 }}>Add</Button>
    </Paper>
  );
}

/**
 * One ticket: title, surface, depends-on, requirements, acceptance criteria.
 * index is 0-based; dependsOn is a 1-based ticket number of an EARLIER ticket.
 * onChange(patch) merges into this ticket. onCopy is wired in T9.
 */
export default function TicketCard({ ticket, index, total, onChange, onCopy, busy }) {
  const reqs = ticket.requirements || [];
  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }} data-testid={`ticket-card-${index + 1}`}>
      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
        <Typography variant="overline" color="text.secondary">
          {total > 1 ? `Ticket ${index + 1} of ${total}` : 'Ticket 1'}
        </Typography>
        <TextField
          variant="standard"
          placeholder="Ticket title"
          value={ticket.title || ''}
          onChange={(e) => onChange({ title: e.target.value })}
          inputProps={{ 'aria-label': `ticket ${index + 1} title` }}
          sx={{ flex: 1, minWidth: 180 }}
        />
        <TextField
          select
          size="small"
          label="Surface"
          value={ticket.surface || 'other'}
          onChange={(e) => onChange({ surface: e.target.value })}
          SelectProps={{ native: true }}
          inputProps={{ 'aria-label': `ticket ${index + 1} surface` }}
          sx={{ minWidth: 110 }}
        >
          {SURFACES.map((s) => <option key={s} value={s}>{s}</option>)}
        </TextField>
        {index > 0 && (
          <TextField
            select
            size="small"
            label="Depends on"
            value={String(ticket.dependsOn || 0)}
            onChange={(e) => onChange({ dependsOn: Number(e.target.value) })}
            SelectProps={{ native: true }}
            inputProps={{ 'aria-label': `ticket ${index + 1} depends on` }}
            sx={{ minWidth: 130 }}
          >
            <option value="0">None</option>
            {Array.from({ length: index }, (_, k) => <option key={k + 1} value={String(k + 1)}>Ticket {k + 1}</option>)}
          </TextField>
        )}
        {onCopy && <Button size="small" onClick={() => onCopy(index + 1)} disabled={busy}>Copy ticket</Button>}
      </Stack>
      <SectionTable
        title="Requirements"
        rows={reqs}
        columns={{ numLabel: '#' }}
        onChange={(rows) => onChange({ requirements: rows })}
        onAdd={() => onChange({ requirements: renum([...reqs, { num: 0, details: '' }]) })}
      />
      <BulletList
        title="Acceptance Criteria"
        items={ticket.acceptanceCriteria || []}
        onChange={(items) => onChange({ acceptanceCriteria: items })}
      />
    </Paper>
  );
}
